import { getFilterGlobalUseCase } from '@/di';
import type { GlobalFilters } from '@/domains/filters/filter.entity';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { FilterStoreModel } from '../models/filterStore.model';

const defaultFilterState: GlobalFilters =
  getFilterGlobalUseCase().getDefaultFilters();

/**
 * Store global de filtros de la aplicación
 * Gestiona año y semestre que afectan todas las vistas
 * Persiste: year y semester en localStorage
 */
export const useFilterStore = create<FilterStoreModel>()(
  persist(
    (set, get) => ({
      ...defaultFilterState,

      /**
       * Resetea filtros globales a sus valores por defecto
       */
      resetFilters: () => {
        set({ ...defaultFilterState });
      },

      /**
       * Obtiene los filtros globales actuales
       *
       * @returns Objeto con year y semester actuales
       */
      getGlobalFilters: (): GlobalFilters => {
        const { year, semester } = get();
        return { year, semester };
      },

      /**
       * Actualiza filtros globales (año y/o semestre)
       * Solo actualiza el estado si hay cambios reales
       *
       * @param filters - Objeto con year y/o semester a actualizar
       */
      setGlobalFilters: (filters: GlobalFilters) => {
        set((state) => {
          const newYear =
            filters.year !== undefined ? filters.year : state.year;
          const newSemester =
            filters.semester !== undefined ? filters.semester : state.semester;

          if (newYear !== state.year || newSemester !== state.semester) {
            return {
              year: newYear,
              semester: newSemester,
            };
          }
          return state;
        });
      },
    }),
    {
      name: '@app/filter',
      partialize: (state) => ({
        semester: state.semester,
        year: state.year,
      }),
    },
  ),
);
